import { Query, QueryClient } from "react-query";

import { Fields } from "../types/fields";

/**
 * Query key prefixes of everything that reads *record data* from the API: the linked records a
 * respondent picks from (`useLinkedRecords`), the related records shown next to them
 * (`useLinkedRecordRelatedRecords`) and the lookup values resolved from a picked record
 * (`useLookupValue`).
 *
 * Deliberately excludes the linked application structure (`…_fields_labels`), members and teams —
 * a submission does not change those.
 */
const RECORD_DATA_QUERY_PREFIXES = [
  `${Fields.linkedrecordfield}_`,
  "lookup_value",
  "lookup_related_records",
];

const STRUCTURE_QUERY_SUFFIX = "_fields_labels";

export const isRecordDataQuery = (query: Query): boolean => {
  // react-query 3 keeps a string key as a plain string (only the hash is array-shaped), so indexing
  // it would yield its first character.
  const { queryKey } = query;
  const head = Array.isArray(queryKey) ? queryKey[0] : queryKey;

  return (
    typeof head === "string" &&
    !head.endsWith(STRUCTURE_QUERY_SUFFIX) &&
    RECORD_DATA_QUERY_PREFIXES.some((prefix) => head.startsWith(prefix))
  );
};

/**
 * Drops the cached record data so the next form the respondent fills in fetches it again.
 *
 * Record queries are cached with `staleTime: Infinity` for the life of the page, and the query
 * client outlives the `Form` that is remounted for "submit another response". A record linked by
 * the submission therefore stayed selectable — its backlink value in the cache was still empty —
 * until the page was reloaded (BUG-21774). Removing rather than invalidating means the new form
 * shows a loading state instead of flashing the stale list first.
 */
export const removeRecordDataQueries = (queryClient: QueryClient): void => {
  queryClient.removeQueries({ predicate: isRecordDataQuery });
};
